<script>
/* eslint no-console: 0 */
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
import {
  ALLOWED_FILE_TYPES,
  MAXIMUM_FILE_UPLOAD_SIZE,
} from 'shared/constants/messages';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import FileUpload from 'vue-upload-component';

export default {
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
    FileUpload,
    AttachmentPreview,
  },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    edcachedUserList: { type: String, default: '' },
    edattachments: { type: Array, default: () => [] },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editCanned: {
        showAlert: false,
        showLoading: false,
      },
      shortCode: this.edshortCode,
      content: this.edcontent,
      cachedUserList: [],
      attachedFiles: this.edattachments || [],
      deAttachedFiles: [],
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required,
    },
  },
  computed: {
    ...mapGetters({
      agentList: 'agents/getAgents',
    }),
    pageTitle() {
      return `${this.$t('CANNED_MGMT.EDIT.TITLE')} - ${this.edshortCode}`;
    },
    allowedFileTypes() {
      return ALLOWED_FILE_TYPES;
    },
  },
  mounted() {
    this.$store.dispatch('agents/get').then(() => {
      this.cachedUserList = this.agentList.filter(item =>
        this.edcachedUserList?.split(',')?.includes(item.id.toString())
      );
    });
  },
  methods: {
    setPageName({ name }) {
      this.v$.content.$touch();
      this.content = name;
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
      this.attachedFiles = [];
    },
    editCannedResponse() {
      // Show loading on button
      this.editCanned.showLoading = true;
      let payloadContent = new FormData();
      payloadContent.append('short_code', this.shortCode);
      payloadContent.append('content', this.content);
      payloadContent.append('cached_user_list', this.cachedUserList.map(item => item.id).join(','));
      this.deAttachedFiles.forEach(file => {
        payloadContent.append(
          'attachments[]',
          JSON.stringify({ blob_id: file.blob_id })
        );
      });
      this.attachedFiles.forEach(
        file =>
          file.resource &&
          payloadContent.append('attachments[]', file.resource.file)
      );
      // Make API Calls
      this.$store
        .dispatch('updateCannedResponse', {
          id: this.id,
          payloadContent: payloadContent,
        })
        .then(() => {
          // Reset Form, Show success message
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => {
            this.onClose();
          }, 10);
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
    onIndirectFileUpload(file) {
      const MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE = MAXIMUM_FILE_UPLOAD_SIZE;
      if (!file) {
        return;
      }
      if (checkFileSizeLimit(file, MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE)) {
        this.attachFile({ file });
      } else {
        useAlert(
          this.$t('CONVERSATION.FILE_SIZE_LIMIT', {
            MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE,
          })
        );
      }
    },
    attachFile({ blob, file }) {
      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          resource: blob || file,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
        });
      };
    },
    removeAttachment(itemIndex) {
      this.deAttachedFiles.push(this.attachedFiles[itemIndex]);
      this.attachedFiles = this.attachedFiles.filter(
        (item, index) => itemIndex !== index
      );
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="pageTitle" />
      <form class="flex flex-col w-full" @submit.prevent="">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
              @input="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>
        <!--Thêm chọn users có thể xem-->
        <div class="w-full">
          <label>
            {{ $t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT') }}
          </label>
          <multiselect
            v-model="cachedUserList"
            :options="agentList"
            track-by="id"
            label="name"
            :multiple="true"
            :placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
            selected-label
            :select-label="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT')"
            :deselect-label="'Bỏ chọn'"
            :allow-empty="true"
            :close-on-select="false"
            :clear-on-select="false"
            :hide-selected="true"
          />
        </div>
        <div class="w-full">
          <FileUpload
            v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
            :accept="allowedFileTypes"
            :data="{
              direct_upload_url: '/rails/active_storage/direct_uploads',
              direct_upload: true,
            }"
            drop
            :drop-directory="false"
            multiple
            :size="4096 * 4096"
            @input-file="onIndirectFileUpload"
          >
            <woot-button
              v-if="true"
              :title="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
              class-names="button--upload"
              color-scheme="secondary"
              emoji="📎"
              icon="attach"
              size="small"
              variant="smooth"
            />
          </FileUpload>
        </div>
        <div v-if="attachedFiles.length > 0" class="attachment-preview-box">
          <AttachmentPreview
            :attachments="attachedFiles"
            @remove-attachment="removeAttachment"
          />
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              editCanned.showLoading
            "
            :is-loading="editCanned.showLoading"
            @click.prevent="editCannedResponse()"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
:deep(.ProseMirror-menubar) {
  @apply hidden;
}

:deep(.ProseMirror-woot-style) {
  @apply min-h-[12.5rem];
}
</style>
